import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Button, Chip, CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuthProviderFetchJson } from '../../lib/Auth';
import { superAdminApiServer } from '../../lib/config';
import { getEmployeeCacheDetailMock } from './cacheMockData';

interface EmployeeCacheDetailItem {
  integrationUuid: string;
  externalId: string;
  matricula: string | null;
  name: string | null;
  sex: string | null;
  cpf: string | null;
  pis: string | null;
  email: string | null;
  phoneNumber: string | null;
  status: string | null;
  isActive: boolean | null;
  startDate: string | null;
  deactivationDate: string | null;
  position: string | null;
  positionExternalId: string | null;
  positionName: string | null;
  department: string | null;
  departmentExternalId: string | null;
  departmentCode: string | null;
  departmentName: string | null;
  subsidiary: string | null;
  subsidiaryExternalId: string | null;
  subsidiaryCode: string | null;
  subsidiaryName: string | null;
  subsidiaryCnpj: string | null;
  costCenter: string | null;
  costCenterExternalId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  supervisorExternalId: string | null;
  scheduleExternalId: string | null;
  scheduleStartDate: string | null;
  holidayGroupExternalId: string | null;
  payrollGroupExternalId: string | null;
  businessRulesGroupExternalId: string | null;
  raw: Record<string, unknown> | null;
  syncedAt: string | null;
  syncedErrorAt: string | null;
  syncedError: string | null;
  updateReason: string | null;
  createdAt: string;
  updatedAt: string;
}

const DetailField = ({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 200, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? '0.8rem' : 'inherit' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const EmployeeCacheDetail = () => {
  const { integrationId, externalId } = useParams<{ integrationId: string; externalId: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const [item, setItem] = useState<EmployeeCacheDetailItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const handleCopyJson = () => {
    if (!item) return;
    navigator.clipboard.writeText(JSON.stringify(item, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    if (!integrationId || !externalId) return;

    if (process.env.NODE_ENV === 'development') {
      setItem(getEmployeeCacheDetailMock(externalId) as EmployeeCacheDetailItem | null);
      setLoading(false);
      return;
    }

    const url = `${superAdminApiServer}/integrations/${integrationId}/cache/employees/${encodeURIComponent(externalId)}`;
    getAuthProviderFetchJson(url)
      .then(({ json }) => setItem(json))
      .catch(() => notify('Failed to load employee cache detail', { type: 'error' }))
      .finally(() => setLoading(false));
  }, [integrationId, externalId, notify]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!item) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography color="text.secondary">Employee cache record not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h6">Employee Cache: {item.name ?? item.externalId}</Typography>
        <Tooltip title={copied ? 'Copied!' : 'Copy JSON payload'}>
          <Button variant="outlined" size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={handleCopyJson}>
            {copied ? 'Copied!' : 'Copy JSON'}
          </Button>
        </Tooltip>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <DetailField label="External ID" value={item.externalId} mono />
            <DetailField label="Matricula" value={item.matricula} mono />
            <DetailField label="Name" value={item.name} />
            <DetailField label="CPF" value={item.cpf} mono />
            <DetailField label="PIS" value={item.pis} mono />
            <DetailField label="Email" value={item.email} />
            <DetailField label="Phone" value={item.phoneNumber} />
            <DetailField label="Status" value={item.status ? <Chip label={item.status} color={item.isActive === false ? 'default' : 'success'} size="small" /> : null} />
            <DetailField label="Active" value={item.isActive != null ? String(item.isActive) : null} />
            <DetailField label="Start Date" value={item.startDate} />
            <DetailField label="Deactivation Date" value={item.deactivationDate} />
            <DetailField label="Synced At" value={item.syncedAt ? new Date(item.syncedAt).toLocaleString() : null} />
            <DetailField label="Last Updated At" value={item.updatedAt ? new Date(item.updatedAt).toLocaleString() : null} />
            <DetailField label="Supervisor External ID" value={item.supervisorExternalId} mono />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <DetailField label="Position" value={item.positionName} />
            <DetailField label="Position External ID" value={item.positionExternalId} mono />
            <DetailField label="Department" value={item.departmentName} />
            <DetailField label="Department External ID" value={item.departmentExternalId} mono />
            <DetailField label="Subsidiary" value={item.subsidiaryName} />
            <DetailField label="Subsidiary Code" value={item.subsidiaryCode} mono />
            <DetailField label="Subsidiary CNPJ" value={item.subsidiaryCnpj} mono />
            <DetailField label="Subsidiary External ID" value={item.subsidiaryExternalId} mono />
            <DetailField label="Cost Center" value={item.costCenterName} />
            <DetailField label="Cost Center Code" value={item.costCenterCode} mono />
            <DetailField label="Cost Center External ID" value={item.costCenterExternalId} mono />
            <DetailField label="Schedule External ID" value={item.scheduleExternalId} mono />
            <DetailField label="Schedule Start Date" value={item.scheduleStartDate} />
            <DetailField label="Holiday Group External ID" value={item.holidayGroupExternalId} mono />
            <DetailField label="Payroll Group External ID" value={item.payrollGroupExternalId} mono />
            <DetailField label="Business Rules Group External ID" value={item.businessRulesGroupExternalId} mono />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmployeeCacheDetail;
